import { readInventoryField, readInventoryRows } from '../compile/inventory-raw-readers';
import { record, upper, type Row } from '../ability/context';
import { statusRows } from '../statuses/expiry';
import { surfaceField } from '../compile/from-base-encounter-media';
import type { ObjectDestroyCommand, ObjectDestroyFrame, SpillRelease } from './destroy-object';

/** Saved SQL facts survive lean compilation on the owning surface's base envelope. */
export function savedFloorInventory(encounter: Row, row: Row, id = upper(readInventoryField(row, 'inventory_id'))): Row | null {
  for (const host of [row, encounter, ...(Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr : [])]) {
    if (!record(host)) continue;
    for (const key of ['_inventory_held', '_inventory_base', '_entity_base']) {
      const base = record(record(host[key])?.base);
      if (!base) continue;
      const rows = [...readInventoryRows(base, 'inventory')];
      const possessor = record(base.possessor);
      if (possessor) rows.push(possessor);
      const seen = new Set<string>();
      let saved: Row | undefined;
      for (let index = 0; index < rows.length; index++) {
        const node = rows[index]!, key = upper(readInventoryField(node, 'inventory_id'));
        if (seen.has(key)) continue;
        seen.add(key);
        if (key === id) { saved = node; break; }
        rows.push(...readInventoryRows(node, 'inventory_in_container'));
      }
      if (saved) return saved;
    }
  }
  return null;
}

export function savedFloorVitality(saved: Row): { v_max: number | null; v_current: number | null; v_temp: number | null } {
  const number = (key: string): number | null => {
    const value = readInventoryField(saved, key);
    return value === null || value === '' ? null : Number(value);
  };
  return { v_max: number('v_max'), v_current: number('v_current'), v_temp: number('v_temp') };
}

export interface FloorInventoryWrite { table: 'inventory'; id: string; before: Row | null; after: Row; kind?: 'spill-delete' | 'spill-release' }
export interface FloorStatusWrite { table: 'statuses'; op: 'update'; row: Row; before: { status_deleted: number } }
export interface FloorFallSave { inventory: FloorInventoryWrite[]; statuses: FloorStatusWrite[] }

/** The fall already chose destruction. Persist that exact source, statuses and placed pile. */
export function floorFallSavePlan(saved: Row, frame: ObjectDestroyFrame, resizedCurrent?: number | null,
  command?: ObjectDestroyCommand, savedRow?: (id: string) => Row | null): FloorFallSave {
  const read = (key: string): unknown => readInventoryField(saved, key);
  const inventory: FloorInventoryWrite[] = [{ table: 'inventory', id: upper(read('inventory_id')),
    before: { inventory_deleted: Number(read('inventory_deleted') ?? 0), item_qty: Number(read('item_qty') ?? 1), v_current: Number(resizedCurrent ?? read('v_current') ?? 0) },
    after: { inventory_deleted: 1, item_qty: 0, v_current: 0 } }];
  for (const row of frame.add) {
    const field = (key: string): unknown => readInventoryField({ inventory: row.inventory }, key);
    const transform = field('transform_3d');
    inventory.push({ table: 'inventory', id: upper(field('inventory_id')), before: null, after: {
      inventory_item_id: field('inventory_item_id'), inventory_name: field('inventory_name'), item_qty: Number(field('item_qty') ?? 1),
      inventory_size: field('inventory_size'), transform_3d: transform == null ? null : JSON.stringify(transform),
      x_pos: Number(row.position.x_pos), y_pos: Number(row.position.y_pos), z_pos: Number(row.position.z_pos),
    } });
  }
  const deleted = new Set(frame.remove.map(upper));
  const releases = new Map<string, SpillRelease>();
  const contents = (node: ObjectDestroyCommand): void => {
    for (const id of node.spill?.delete ?? []) deleted.add(upper(id));
    for (const release of node.spill?.release ?? []) releases.set(upper(release.inventory_id), release);
    for (const child of node.spill?.destroyed ?? []) contents(child);
  };
  if (command) contents(command);
  const retired = [saved];
  const raw = (id: string): Row => {
    const row = savedRow?.(id);
    if (!row) throw new Error(`fall-spill-saved-row-not-held:${id}`);
    return row;
  };
  for (const id of deleted) {
    if (id === upper(read('inventory_id'))) continue;
    const row = raw(id), field = (key: string): unknown => readInventoryField(row, key);
    inventory.push({ table: 'inventory', id, kind: 'spill-delete',
      before: { inventory_deleted: Number(field('inventory_deleted') ?? 0), item_qty: Number(field('item_qty') ?? 1), v_current: savedFloorVitality(row).v_current },
      after: { inventory_deleted: 1, item_qty: 0, v_current: 0 } });
    retired.push(row);
  }
  for (const released of frame.released ?? []) {
    const id = upper(readInventoryField(released, 'inventory_id')), row = raw(id);
    const release = releases.get(id);
    if (!release) throw new Error(`fall-spill-release-not-planned:${id}`);
    const field = (key: string): unknown => readInventoryField(row, key);
    const vitality = savedFloorVitality(row);
    inventory.push({ table: 'inventory', id, kind: 'spill-release', before: {
      possess_id: String(field('possess_id') ?? ''), equipped: String(field('equipped') ?? ''),
      x_pos: field('x_pos'), y_pos: field('y_pos'), z_pos: field('z_pos'), v_current: vitality.v_current, v_temp: vitality.v_temp,
    }, after: { equipped: '', x_pos: release.x_pos, y_pos: release.y_pos, z_pos: release.z_pos,
      v_current: release.v_current, v_temp: release.v_temp } });
  }
  const statuses: FloorStatusWrite[] = retired.flatMap(row => statusRows(row, 'target')).map(status => ({ table: 'statuses', op: 'update',
    row: { status_id: upper(surfaceField(status, 'status_id')), status_deleted: 1 },
    before: { status_deleted: Number(surfaceField(status, 'status_deleted') ?? 0) } }));
  return { inventory, statuses };
}
